import React, { useContext } from "react";
import styled from "@emotion/styled";
import Container from "@src/components/Container";
import { mediaQueries } from "@src/styles/theme";
import { theme, Button } from "@src/components/nessie-web";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import curveSVG from "@src/assets/images/schools/video-curve.svg";

const {
  colors: { dt_aqua20, dt_taro90 },
} = theme;

const SchoolwideSection = styled.section`
  background-color: ${dt_aqua20};
  padding: 54px 0;
  background-image: url(${curveSVG});
  background-repeat: no-repeat;
  background-position: top center;
  background-size: 100%;

  ${mediaQueries[0]} {
    padding: 108px 0;
    background-size: 102% auto, cover;
    background-position: top center, center;
  }
`;

const SchoolwideWrapper = styled.div`
  display: grid;
  grid-gap: 30px;
  grid-template-columns: repeat(4, 1fr);
  justify-content: center;
  align-items: center;
`;

const SchoolwideTitle = styled.h2`
  font-size: 28px;
  line-height: 36px;
  letter-spacing: -0.2px;
  font-weight: 800;
  text-align: center;
  color: ${dt_taro90};
  margin: 0;
  grid-column: 1 / 5;

  ${mediaQueries[0]} {
    font-size: 44px;
    line-height: 48px;
    letter-spacing: -0.4px;
    padding-top: 54px;
  }
`;

const SchoolwideContent = styled.div`
  grid-column: 1 / 5;

  ${mediaQueries[0]} {
    grid-column: 1 / 3;
  }
`;

const ButtonWrapper = styled.div`
  display: grid;
  gap: 12px;
  align-items: center;
  position: unset;
  width: 100%;

  ${mediaQueries[0]} {
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    justify-content: flex-start;
  }
`;

const SchoolwideItem = styled.div`
  padding-bottom: 18px;
`;

const SchoolwideNumber = styled.span`
  display: inline-block;
  vertical-align: middle;
  width: 40px;
  height: 40px;
  background: #ffffff;
  border-radius: 50%;
  margin-right: 12px;
  font-weight: 800;
  color: #2c2a50;
  text-align: center;
  line-height: 40px;
  font-size: 1.4rem;
`;

const SchoolwideText = styled.span`
  display: inline-block;
  vertical-align: middle;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: -0.25px;
  color: #2c2a50;
  width: calc(100% - 52px);
`;

const SchoolwideImage = styled.img`
  grid-column: 1 / 5;
  grid-row: 1;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  ${mediaQueries[0]} {
    grid-column: 3 / 5;
    grid-row: 2;
  }
`;

type RedesignSchoolwideSectionProps = {
  schoolwide_title: string;
  schoolwide_items: any;
  translationPath: string;
  schoolwide_button_text: string;
  schoolwide_button_link: string;
  schoolwide_image: any;
  schoolwide_image_alt: string;
};

const RedesignSchoolwideSection: React.FC<RedesignSchoolwideSectionProps> = ({
  schoolwide_title,
  schoolwide_items,
  translationPath,
  schoolwide_button_text,
  schoolwide_button_link,
  schoolwide_image,
  schoolwide_image_alt,
}) => {
  const t = useContext(TranslationContext);

  return (
    <>
      <SchoolwideSection>
        <Container>
          <SchoolwideWrapper>
            <SchoolwideTitle>{t.translate(schoolwide_title)}</SchoolwideTitle>
            <SchoolwideContent>
              {schoolwide_items.map((item: any, index: number) => (
                <SchoolwideItem key={index}>
                  <SchoolwideNumber>{index + 1}</SchoolwideNumber>
                  <SchoolwideText>{t.translate(`${translationPath}text_${index + 1}`)}</SchoolwideText>
                </SchoolwideItem>
              ))}
              <ButtonWrapper>
                <Button href={schoolwide_button_link}>{t.translate(schoolwide_button_text)}</Button>
              </ButtonWrapper>
            </SchoolwideContent>
            <SchoolwideImage src={schoolwide_image.file.publicURL} alt={schoolwide_image_alt} />
          </SchoolwideWrapper>
        </Container>
      </SchoolwideSection>
    </>
  );
};

export default RedesignSchoolwideSection;
